import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Attachment, AttachmentEntity, AttachmentList, ActualsMonth, Suggestion, XeroBankMonth, XeroDoc, XeroPlLine, XeroSpend, DriveSettings, DriveState, Minutes, ReminderSettings, ReminderState, CalItem, Meeting, Pack, PackDocument, PackIssueSummary, PackSection, PackSettings, MeasureDef, MeasureSettings, Release, ReleaseBody, CompanyDates, Obligation, ObligationBody, StatutoryPreset, Risk, RiskBody, RiskIssue, RiskIssueSummary, RiskScale, Action, ActionBody, ActionUpdateBody, Decision, DecisionAction, DecisionBody, RegisterSettings, RegistersModel, AuthorityRulesBody, Connections, SourceSync, AuditEntry, AuthConfig, BankBalance, Commitment, CommitmentBody, FinanceModel, FinanceSettings, GateThresholds, Lever, LeverAction, LeverBody, Me, ReapplyResult, Snapshot, SnapshotBody, SnapshotSummary, User } from "@otii/gov-shared";
import { ApiError, api, del, get, post, put } from "@otii/ui/api";

export const keys = {
  me: ["me"] as const,
  authConfig: ["auth-config"] as const,
  model: ["finance-model"] as const,
  history: (id: string) => ["lever-history", id] as const,
  users: ["admin-users"] as const,
  thresholds: ["admin-thresholds"] as const,
  audit: ["admin-audit"] as const,
  snapshots: ["snapshots"] as const,
  snapshot: (id: string) => ["snapshot", id] as const,
  reapply: (id: string) => ["snapshot-reapply", id] as const,
  connections: ["admin-connections"] as const,
  registers: ["registers"] as const,
  decisionHistory: (id: string) => ["decision-history", id] as const,
  actionHistory: (id: string) => ["action-history", id] as const,
  calendarFeed: ["calendar-feed"] as const,
  riskIssues: ["risk-issues"] as const,
  riskIssue: (id: string) => ["risk-issue", id] as const,
  riskHistory: (id: string) => ["risk-history", id] as const,
  obligationHistory: (id: string) => ["obligation-history", id] as const,
  releaseHistory: (id: string) => ["release-history", id] as const,
  measureHistory: (id: string) => ["measure-history", id] as const,
  packIssues: ["pack-issues"] as const,
  packIssue: (id: string) => ["pack-issue", id] as const,
  packHistory: (id: string) => ["pack-history", id] as const,
  calendar: (from: string, to: string) => ["calendar", from, to] as const,
  meetingHistory: (id: string) => ["meeting-history", id] as const,
  reminders: ["admin-reminders"] as const,
  minutes: (id: string) => ["minutes", id] as const,
  minutesHistory: (id: string) => ["minutes-history", id] as const,
  drive: ["records-drive"] as const,
  actuals: ["finance-actuals"] as const,
  suggestions: ["finance-suggestions"] as const,
  sharedDrives: ["shared-drives"] as const,
  attachments: (entity: string, id: string) => ["attachments", entity, id] as const,
  attachmentCounts: ["attachment-counts"] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => get<Me>("/api/auth/me"), retry: false, staleTime: 60_000 });
export const useAuthConfig = () => useQuery({ queryKey: keys.authConfig, queryFn: () => get<AuthConfig>("/api/auth/config") });
export const useFinanceModel = (enabled = true) => useQuery({ queryKey: keys.model, queryFn: () => get<FinanceModel>("/api/finance/model"), enabled });
export const useLeverHistory = (id: string | null) => useQuery({ queryKey: keys.history(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/finance/levers/${id}/history`), enabled: !!id });

/** A write that refreshes the finance model when it lands, and toasts what went wrong if it doesn't. */
function useFinanceWrite<V, R>(fn: (v: V) => Promise<R>, done?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: keys.model });
      qc.invalidateQueries({ queryKey: ["lever-history"] });
      qc.invalidateQueries({ queryKey: keys.audit });
      // a lever's proposal and decision live in the register too
      qc.invalidateQueries({ queryKey: keys.registers });
      qc.invalidateQueries({ queryKey: ["decision-history"] });
      if (done) toast.success(typeof done === "string" ? done : done(r));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useAddBalance = () => useFinanceWrite((b: { asAt: string; amount: number; note: string }) => post<BankBalance>("/api/finance/balances", b), "Bank balance saved");
export const useSaveCommitment = () =>
  useFinanceWrite(({ id, body }: { id: string | null; body: CommitmentBody }) => (id ? put<Commitment>(`/api/finance/commitments/${id}`, body) : post<Commitment>("/api/finance/commitments", body)), "Line saved");
export const useCommitmentActive = () =>
  useFinanceWrite(({ id, active }: { id: string; active: boolean }) => post(`/api/finance/commitments/${id}/active`, { active }), (r) => "Saved");
export const useSaveLever = () => useFinanceWrite(({ id, body }: { id: string | null; body: LeverBody }) => (id ? put<Lever>(`/api/finance/levers/${id}`, body) : post<Lever>("/api/finance/levers", body)));
export const useDeleteLever = () => useFinanceWrite((id: string) => del(`/api/finance/levers/${id}`), "Draft deleted");
export const useLeverAction = () => useFinanceWrite(({ id, action, note }: { id: string; action: LeverAction; note?: string }) => post<Lever>(`/api/finance/levers/${id}/status`, { action, note }));
export const useApplyLever = () => useFinanceWrite(({ id, applied }: { id: string; applied: boolean }) => post<Lever>(`/api/finance/levers/${id}/apply`, { applied }));

export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => get<(User & { lastLoginAt: string | null })[]>("/api/admin/users") });
export const useThresholdVersions = () => useQuery({ queryKey: keys.thresholds, queryFn: () => get<GateThresholds[]>("/api/admin/thresholds") });
export const useAudit = () => useQuery({ queryKey: keys.audit, queryFn: () => get<AuditEntry[]>("/api/admin/audit?limit=300") });

function useAdminWrite<V, R>(fn: (v: V) => Promise<R>, done: string, extra: readonly (readonly string[])[] = []) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of [keys.model, keys.audit, keys.registers, ...extra]) qc.invalidateQueries({ queryKey: k });
      toast.success(done);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useAddUser = () => useAdminWrite((b: { email: string; name: string; role: User["role"]; isAdmin: boolean }) => post<User>("/api/admin/users", b), "Person added", [keys.users]);
export const useInviteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<{ to: string; expiresAt: string }>(`/api/admin/users/${id}/invite`),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: keys.audit });
      toast.success(`Invitation sent to ${r.to}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
};
export const useUpdateUser = () =>
  useAdminWrite(({ id, patch }: { id: string; patch: Partial<Pick<User, "name" | "role" | "isAdmin" | "active">> }) => api<User>(`/api/admin/users/${id}`, { method: "PATCH", body: patch }), "Saved", [keys.users, keys.me]);
export const useAddThresholds = () =>
  useAdminWrite((b: { effectiveFrom: string; green: number; amber: number; red: number; note: string }) => post<GateThresholds>("/api/admin/thresholds", b), "Thresholds saved", [keys.thresholds]);
export const useSaveSettings = () => useAdminWrite((b: FinanceSettings) => put("/api/admin/settings", b), "Settings saved");
export const useSaveList = () =>
  useAdminWrite(({ which, items }: { which: "categories" | "lever-kinds"; items: { id?: string; name: string; active: boolean; direction?: "in" | "out" }[] }) => put(`/api/admin/${which}`, { items }), "List saved");

/* ------------------------------------------------------------------ */
/* Snapshots                                                           */
/* ------------------------------------------------------------------ */

export const useSnapshots = () => useQuery({ queryKey: keys.snapshots, queryFn: () => get<SnapshotSummary[]>("/api/finance/snapshots") });
export const useSnapshot = (id: string | undefined) => useQuery({ queryKey: keys.snapshot(id ?? ""), queryFn: () => get<Snapshot>(`/api/finance/snapshots/${id}`), enabled: !!id, staleTime: Infinity });
/** What re-applying would do, read fresh each time the dialog opens. */
export const useReapplyPlan = (id: string, enabled: boolean) => useQuery({ queryKey: keys.reapply(id), queryFn: () => get<ReapplyResult>(`/api/finance/snapshots/${id}/reapply`), enabled, staleTime: 0, gcTime: 0 });

function useSnapshotWrite<V, R>(fn: (v: V) => Promise<R>, done?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of [keys.snapshots, keys.model, keys.audit]) qc.invalidateQueries({ queryKey: k });
      qc.invalidateQueries({ queryKey: ["snapshot"] });
      qc.invalidateQueries({ queryKey: ["lever-history"] });
      if (done) toast.success(done);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useTakeSnapshot = () => useSnapshotWrite((b: SnapshotBody) => post<Snapshot>("/api/finance/snapshots", b));
export const useUpdateSnapshot = () => useSnapshotWrite(({ id, patch }: { id: string; patch: { name?: string; note?: string } }) => api<Snapshot>(`/api/finance/snapshots/${id}`, { method: "PATCH", body: patch }), "Saved");
export const useDeleteSnapshot = () => useSnapshotWrite((id: string) => del(`/api/finance/snapshots/${id}`), "Snapshot deleted");
export const useReapply = () => useSnapshotWrite((id: string) => post<ReapplyResult>(`/api/finance/snapshots/${id}/reapply`));

/* ------------------------------------------------------------------ */
/* Xero and the Sales Hub                                              */
/* ------------------------------------------------------------------ */

export interface CalendarConnection {
  mode: "live" | "demo" | "off";
  ready: boolean;
  connected: boolean;
  email: string | null;
  error: string | null;
  settings: { auto: boolean };
  sync: SourceSync | null;
  redirectUri: string;
  drive: { mode: "live" | "demo" | "off"; scoped: boolean };
}
export const useConnections = () =>
  useQuery({ queryKey: keys.connections, queryFn: () => get<{ state: Connections; xero: { redirectUri: string; scopes: string | null }; calendar: CalendarConnection }>("/api/admin/connections") });
export function useCalendarConnect() {
  return useMutation({
    mutationFn: () => post<{ url: string }>("/api/admin/connections/google/connect"),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}
export const useCalendarDisconnect = () => useAdminWrite(() => del("/api/admin/connections/google"), "Google Calendar disconnected", [keys.connections]);
export const useCalendarSettings = () => useAdminWrite((b: { auto: boolean }) => put("/api/admin/connections/google/settings", b), "Saved", [keys.connections]);
export const useCalendarSync = () => useAdminWrite(() => post<SourceSync>("/api/admin/connections/google/sync"), "Invites sent", [keys.connections, ["calendar"]]);

/** Read Xero and the Sales Hub again now. */
export function useSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ xero: SourceSync; hub: SourceSync }>("/api/finance/sync"),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: keys.model });
      qc.invalidateQueries({ queryKey: keys.connections });
      qc.invalidateQueries({ queryKey: keys.actuals });
      qc.invalidateQueries({ queryKey: keys.suggestions });
      const failed = [r.xero.ok || /off here/.test(r.xero.detail) ? null : `Xero: ${r.xero.detail}`, r.hub.ok || /off here/.test(r.hub.detail) ? null : `Sales Hub: ${r.hub.detail}`].filter(Boolean);
      if (failed.length) toast.error(failed.join(" · "));
      else toast.success("Synced");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export function useXeroConnect() {
  return useMutation({
    mutationFn: () => post<{ url: string }>("/api/admin/connections/xero/connect"),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useXeroDisconnect = () => useAdminWrite(() => del("/api/admin/connections/xero"), "Xero disconnected", [keys.connections]);

export const useSaveAuthority = () => useAdminWrite((rules: AuthorityRulesBody["rules"]) => put("/api/admin/authority", { rules }), "Approval rules saved");

/* ------------------------------------------------------------------ */
/* Decisions and actions                                               */
/* ------------------------------------------------------------------ */

export const useRegisters = (enabled = true) => useQuery({ queryKey: keys.registers, queryFn: () => get<RegistersModel>("/api/registers"), enabled });
export const useDecisionHistory = (id: string | null) => useQuery({ queryKey: keys.decisionHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/decisions/${id}/history`), enabled: !!id });
export const useActionHistory = (id: string | null) => useQuery({ queryKey: keys.actionHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/actions/${id}/history`), enabled: !!id });

/** A write to the registers: refreshes them (and finance, which a lever's decision moves) and toasts what went wrong. */
function useRegisterWrite<V, R>(fn: (v: V) => Promise<R>, done?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      for (const k of [keys.registers, keys.model, keys.audit]) qc.invalidateQueries({ queryKey: k });
      for (const k of ["decision-history", "action-history", "lever-history"]) qc.invalidateQueries({ queryKey: [k] });
      if (done) toast.success(typeof done === "string" ? done : done(r));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useSaveDecision = () => useRegisterWrite(({ id, body }: { id: string | null; body: DecisionBody }) => (id ? put<Decision>(`/api/decisions/${id}`, body) : post<Decision>("/api/decisions", body)));
export const useDeleteDecision = () => useRegisterWrite((id: string) => del(`/api/decisions/${id}`), "Draft deleted");
export const useDecisionAction = () => useRegisterWrite(({ id, action, note }: { id: string; action: DecisionAction; note?: string }) => post<Decision>(`/api/decisions/${id}/status`, { action, note }));
export const useRecordDecision = () =>
  useRegisterWrite(({ id, deciderIds, takenOn, note }: { id: string; deciderIds: string[]; takenOn: string; note?: string }) => post<Decision>(`/api/decisions/${id}/record`, { deciderIds, takenOn, note }), "Recorded");
export const useRecordConsent = () =>
  useRegisterWrite(
    ({ id, ...body }: { id: string; partyId: string; given: boolean; date: string; documentUrl: string | null; note: string | null }) => post<Decision>(`/api/decisions/${id}/consents`, body),
    "Consent recorded",
  );
export const useDecisionDates = () =>
  useRegisterWrite(
    ({ id, ...body }: { id: string; reviewBy: string | null; finalAfter: string | null; reviewed: boolean; note?: string }) => api<Decision>(`/api/decisions/${id}/dates`, { method: "PATCH", body }),
    (d) => (d.lastReviewedAt ? "Saved" : "Dates saved"),
  );

export const useSaveAction = () =>
  useRegisterWrite(({ id, body }: { id: string | null; body: ActionBody | ActionUpdateBody }) => (id ? put<Action>(`/api/actions/${id}`, body) : post<Action>("/api/actions", body)));
export const useDeleteAction = () => useRegisterWrite((id: string) => del(`/api/actions/${id}`), "Action deleted");
export const useCloseAction = () => useRegisterWrite(({ id, note, link }: { id: string; note: string | null; link: string | null }) => post<Action>(`/api/actions/${id}/close`, { note, link }), "Closed");
export const useCheckAction = () => useRegisterWrite((id: string) => post<Action>(`/api/actions/${id}/check`), "Confirmed");
export const useReopenAction = () => useRegisterWrite(({ id, reason }: { id: string; reason: string }) => post<Action>(`/api/actions/${id}/reopen`, { reason }), "Reopened");
export const useBulkActions = () =>
  useRegisterWrite(
    (body: { ids: string[]; ownerId?: string | null; move?: { due: string | null; milestoneId: string | null; reason?: string } }) => post<{ changed: number }>("/api/actions/bulk", body),
    (r) => `${r.changed} changed`,
  );

export const useSaveDecisionTypes = () =>
  useAdminWrite((items: { id?: string; name: string; consentGroupId: string | null; active: boolean }[]) => put("/api/admin/decision-types", { items }), "Decision types saved");
export const useSaveConsentGroups = () =>
  useAdminWrite(
    (groups: { id?: string; name: string; comparator: "over" | "at_least"; pct: number; active: boolean; parties: { id?: string; name: string; weight: number; active: boolean }[] }[]) => put("/api/admin/consent-groups", { groups }),
    "Consent groups saved",
  );
export const useSaveMilestones = () => useAdminWrite((items: { id?: string; name: string; date: string | null; active: boolean }[]) => put("/api/admin/milestones", { items }), "Milestones saved");
export const useSaveRegisterSettings = () => useAdminWrite((b: RegisterSettings) => put("/api/admin/register-settings", b), "Settings saved");

/* ------------------------------------------------------------------ */
/* Calendar feed                                                       */
/* ------------------------------------------------------------------ */

export const useCalendarFeed = (enabled: boolean) => useQuery({ queryKey: keys.calendarFeed, queryFn: () => get<{ url: string | null }>("/api/calendar/feed"), enabled });
export function useResetCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ url: string | null }>("/api/calendar/feed/reset"),
    onSuccess: (r) => {
      qc.setQueryData(keys.calendarFeed, r);
      toast.success("New link made. The old one no longer works.");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

/* ------------------------------------------------------------------ */
/* Risks                                                               */
/* ------------------------------------------------------------------ */

export const useRiskIssues = () => useQuery({ queryKey: keys.riskIssues, queryFn: () => get<RiskIssueSummary[]>("/api/risks/issues") });
export const useRiskIssue = (id: string | undefined) => useQuery({ queryKey: keys.riskIssue(id ?? ""), queryFn: () => get<RiskIssue>(`/api/risks/issues/${id}`), enabled: !!id, staleTime: Infinity });
export const useRiskHistory = (id: string | null) => useQuery({ queryKey: keys.riskHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/risks/${id}/history`), enabled: !!id });

function useRiskWrite<V, R>(fn: (v: V) => Promise<R>, done?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      for (const k of [keys.registers, keys.riskIssues, keys.audit]) qc.invalidateQueries({ queryKey: k });
      qc.invalidateQueries({ queryKey: ["risk-history"] });
      if (done) toast.success(typeof done === "string" ? done : done(r));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useSaveRisk = () => useRiskWrite(({ id, body }: { id: string | null; body: RiskBody }) => (id ? put<Risk>(`/api/risks/${id}`, body) : post<Risk>("/api/risks", body)));
export const useReviewRisks = () => useRiskWrite((ids: string[]) => post<{ reviewed: number }>("/api/risks/review", { ids }), (r) => (r.reviewed === 1 ? "Reviewed" : `${r.reviewed} reviewed`));
export const useCloseRisk = () => useRiskWrite(({ id, note }: { id: string; note: string }) => post<Risk>(`/api/risks/${id}/close`, { note }), "Closed");
export const useReopenRisk = () => useRiskWrite((id: string) => post<Risk>(`/api/risks/${id}/reopen`), "Reopened");
export const useIssueRegister = () => useRiskWrite((b: { version: string; note: string }) => post<RiskIssue>("/api/risks/issues", b), (r) => `Issued v${r.version}`);

export const useSaveRiskScale = () => useAdminWrite((scale: RiskScale) => put("/api/admin/risk-scale", scale), "Scale saved");
export const useSaveRiskDomains = () => useAdminWrite((items: { id?: string; name: string; code: string; active: boolean }[]) => put("/api/admin/risk-domains", { items }), "Areas saved");
export const useSaveRiskTiers = () => useAdminWrite((items: { id?: string; name: string; reviewMonths: number; active: boolean }[]) => put("/api/admin/risk-tiers", { items }), "Tiers saved");

/* ------------------------------------------------------------------ */
/* Obligations                                                         */
/* ------------------------------------------------------------------ */

export const useObligationHistory = (id: string | null) => useQuery({ queryKey: keys.obligationHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/obligations/${id}/history`), enabled: !!id });

function useObligationWrite<V, R>(fn: (v: V) => Promise<R>, done?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of [keys.registers, keys.audit]) qc.invalidateQueries({ queryKey: k });
      qc.invalidateQueries({ queryKey: ["obligation-history"] });
      if (done) toast.success(done);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useSaveObligation = () => useObligationWrite(({ id, body }: { id: string | null; body: ObligationBody }) => (id ? put<Obligation>(`/api/obligations/${id}`, body) : post<Obligation>("/api/obligations", body)));
export const useCompleteObligation = () =>
  useObligationWrite(({ id, ...body }: { id: string; doneOn: string; skipped: boolean; note: string | null; link: string | null }) => post<Obligation>(`/api/obligations/${id}/complete`, body), "Recorded");
export const useUndoCompletion = () => useObligationWrite((id: string) => post<Obligation>(`/api/obligations/${id}/undo`), "Taken back");

export const useSaveCompany = () => useAdminWrite((b: CompanyDates) => put("/api/admin/company", b), "Company dates saved");
export const useSaveObligationCategories = () => useAdminWrite((items: { id?: string; name: string; active: boolean }[]) => put("/api/admin/obligation-categories", { items }), "Categories saved");
export function useAddStatutory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (presets: StatutoryPreset[]) => post<{ added: number }>("/api/admin/statutory", { presets }),
    onSuccess: (r) => {
      for (const k of [keys.registers, keys.audit]) qc.invalidateQueries({ queryKey: k });
      toast.success(r.added === 1 ? "1 added" : `${r.added} added`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

/* ------------------------------------------------------------------ */
/* Release log                                                         */
/* ------------------------------------------------------------------ */

export const useReleaseHistory = (id: string | null) => useQuery({ queryKey: keys.releaseHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/releases/${id}/history`), enabled: !!id });
export function useSaveRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: ReleaseBody }) => (id ? put<Release>(`/api/releases/${id}`, body) : post<Release>("/api/releases", body)),
    onSuccess: () => {
      for (const k of [keys.registers, keys.audit]) qc.invalidateQueries({ queryKey: k });
      qc.invalidateQueries({ queryKey: ["release-history"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}
export const useSaveReleaseFrameworks = () =>
  useAdminWrite((items: { id?: string; name: string; detectDays: number | null; deployDays: number | null; active: boolean }[]) => put("/api/admin/release-frameworks", { items }), "Frameworks saved");
export const useSaveReviewers = () => useAdminWrite((items: { id?: string; name: string; role: string; active: boolean }[]) => put("/api/admin/reviewers", { items }), "Reviewers saved");

/* ------------------------------------------------------------------ */
/* Measures                                                            */
/* ------------------------------------------------------------------ */

export const useMeasureHistory = (id: string | null) => useQuery({ queryKey: keys.measureHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/measures/${id}/history`), enabled: !!id });
function useMeasureWrite<V, R>(fn: (v: V) => Promise<R>, done?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      for (const k of [keys.registers, keys.audit]) qc.invalidateQueries({ queryKey: k });
      qc.invalidateQueries({ queryKey: ["measure-history"] });
      if (done) toast.success(typeof done === "string" ? done : done(r));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}
export const useCaptureMonth = () =>
  useMeasureWrite(
    (month: string) => post<{ pulled: number; kept: number }>("/api/measures/capture", { month }),
    (r) => `${r.pulled} pulled${r.kept ? ` · ${r.kept} kept` : ""}`,
  );
export const useConfirmMonth = () => useMeasureWrite((month: string) => post<{ confirmed: number }>("/api/measures/confirm", { month }), (r) => `${r.confirmed} confirmed`);
export const useSetMeasureValue = () => useMeasureWrite(({ id, month, value }: { id: string; month: string; value: number | null }) => put(`/api/measures/${id}/values/${month}`, { value }), "Saved");
export const useConfirmMeasure = () => useMeasureWrite(({ id, month }: { id: string; month: string }) => post(`/api/measures/${id}/values/${month}/confirm`), "Confirmed");
export const useSetMeasureNote = () => useMeasureWrite(({ id, month, note }: { id: string; month: string; note: string }) => put(`/api/measures/${id}/values/${month}/note`, { note }), "Note saved");
export const useSetMeasureTargets = () =>
  useMeasureWrite(({ id, targets }: { id: string; targets: { month: string; value: number | null }[] }) => put(`/api/measures/${id}/targets`, { targets }), "Targets saved");
export type MeasureDefItem = Omit<MeasureDef, "id" | "sortOrder"> & { id?: string };
export const useSaveMeasureDefs = () => useAdminWrite((items: MeasureDefItem[]) => put("/api/admin/measures", { items }), "Measures saved");
export const useSaveMeasureSettings = () => useAdminWrite((body: MeasureSettings) => put("/api/admin/measure-settings", body), "Thresholds saved");

/* ------------------------------------------------------------------ */
/* Monthly pack                                                        */
/* ------------------------------------------------------------------ */

export const usePackIssues = (enabled = true) => useQuery({ queryKey: keys.packIssues, queryFn: () => get<PackIssueSummary[]>("/api/pack-issues"), enabled });
export const usePackIssue = (id: string | undefined) =>
  useQuery({ queryKey: keys.packIssue(id ?? ""), queryFn: () => get<{ summary: PackIssueSummary; document: PackDocument }>(`/api/pack-issues/${id}`), enabled: !!id, staleTime: Infinity });
export const usePackHistory = (id: string | null) => useQuery({ queryKey: keys.packHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/packs/${id}/history`), enabled: !!id });
function usePackWrite<V, R>(fn: (v: V) => Promise<R>, done?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      for (const k of [keys.registers, keys.audit, keys.packIssues]) qc.invalidateQueries({ queryKey: k });
      qc.invalidateQueries({ queryKey: ["pack-history"] });
      if (done) toast.success(typeof done === "string" ? done : done(r));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}
export interface MinutesView {
  meeting: Meeting;
  minutes: Minutes;
  due: string;
  signerId: string | null;
}
export const useMinutes = (meetingId: string | null) => useQuery({ queryKey: keys.minutes(meetingId ?? ""), queryFn: () => get<MinutesView>(`/api/minutes/${meetingId}`), enabled: !!meetingId, retry: false });
export const useMinutesHistory = (meetingId: string | null) => useQuery({ queryKey: keys.minutesHistory(meetingId ?? ""), queryFn: () => get<AuditEntry[]>(`/api/minutes/${meetingId}/history`), enabled: !!meetingId });
function useMinutesWrite<V extends { meetingId: string }>(fn: (v: V) => Promise<MinutesView>, done?: string | ((r: MinutesView) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, v) => {
      qc.setQueryData(keys.minutes(v.meetingId), r);
      for (const k of [keys.registers, keys.audit, keys.minutesHistory(v.meetingId)]) qc.invalidateQueries({ queryKey: k });
      if (done) toast.success(typeof done === "string" ? done : done(r));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}
export type MinutesBody = { chairId: string | null; present: string; apologies: string; discussion: string; decisions: string; notesUrl: string | null };
export const useSaveMinutes = () => useMinutesWrite(({ meetingId, ...body }: MinutesBody & { meetingId: string }) => put<MinutesView>(`/api/minutes/${meetingId}`, body));
export const useSubmitMinutes = () => useMinutesWrite(({ meetingId }: { meetingId: string }) => post<MinutesView>(`/api/minutes/${meetingId}/submit`), "Sent for sign-off");
export const useSignMinutes = () => useMinutesWrite(({ meetingId }: { meetingId: string }) => post<MinutesView>(`/api/minutes/${meetingId}/sign`), "Signed off");
export const useReturnMinutes = () => useMinutesWrite(({ meetingId, note }: { meetingId: string; note: string }) => post<MinutesView>(`/api/minutes/${meetingId}/return`, { note }), "Sent back");
export const useCirculateMinutes = () =>
  useMinutesWrite(
    ({ meetingId }: { meetingId: string }) => post<MinutesView & { sentTo: number }>(`/api/minutes/${meetingId}/circulate`),
    (r) => ((r as MinutesView & { sentTo: number }).sentTo ? `Circulated to ${(r as MinutesView & { sentTo: number }).sentTo}` : "Circulated"),
  );
export interface ActualsState {
  mode: "live" | "demo" | "off";
  history: { plEarliest: string | null; plDone: boolean; bankEarliest: string | null; bankDone: boolean; readAt: string | null };
  months: ActualsMonth[];
  pl: XeroPlLine[];
  bank: XeroBankMonth[];
  bills: Pick<XeroDoc, "kind" | "contactId" | "contactName" | "date" | "total">[];
  spends: XeroSpend[];
}
export const useActuals = () => useQuery({ queryKey: keys.actuals, queryFn: () => get<ActualsState>("/api/finance/actuals") });
export const useSuggestions = () => useQuery({ queryKey: keys.suggestions, queryFn: () => get<Suggestion[]>("/api/finance/suggestions") });
export const useDismissSuggestion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: { contactId: string; name: string }) => post(`/api/finance/suggestions/${encodeURIComponent(s.contactId)}/dismiss`, { name: s.name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.suggestions }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
};
export const useImportNotes = () =>
  useMutation({
    mutationFn: (meetingId: string) => post<{ text: string; title: string | null }>(`/api/minutes/${meetingId}/import`),
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
export const useDrive = (enabled = true) => useQuery({ queryKey: keys.drive, queryFn: () => get<DriveState>("/api/records/drive"), enabled });
export const useSharedDrives = (enabled: boolean) => useQuery({ queryKey: keys.sharedDrives, queryFn: () => get<{ id: string; name: string }[]>("/api/admin/drive/shared-drives"), enabled, retry: false });
export const useSaveDrive = () => useAdminWrite((body: DriveSettings) => put("/api/admin/drive", body), "Saved", [keys.drive]);
export const useSyncDrive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<SourceSync>("/api/admin/drive/sync"),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: keys.drive });
      (r.ok ? toast.success : toast.error)(r.detail);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
};
export const useOpenPack = () => usePackWrite((b: { month: string; kind?: "monthly" | "board" }) => post<Pack>("/api/packs", b), "Pack opened");
export const useSetPackDates = () => usePackWrite(({ id, ...d }: { id: string; meetingOn: string; dueOn: string }) => put<Pack>(`/api/packs/${id}/dates`, d), "Dates saved");
const sectionUrl = (v: { id: string; sectionId: string }, what = "") => `/api/packs/${v.id}/sections/${v.sectionId}${what}`;
export const useSaveSection = () => usePackWrite((v: { id: string; sectionId: string; fields: Record<string, string> }) => put<Pack>(sectionUrl(v), { fields: v.fields }), "Saved");
export const useSubmitSection = () => usePackWrite((v: { id: string; sectionId: string }) => post<Pack>(sectionUrl(v, "/submit")), "Submitted");
export const useApproveSection = () => usePackWrite((v: { id: string; sectionId: string }) => post<Pack>(sectionUrl(v, "/approve")), "Approved");
export const useReturnSection = () => usePackWrite((v: { id: string; sectionId: string; note: string }) => post<Pack>(sectionUrl(v, "/return"), { note: v.note }), "Sent back");
export const useIssuePack = () => usePackWrite((id: string) => post<{ issueId: string; nextPackId: string | null }>(`/api/packs/${id}/issue`));
export const useReopenPack = () => usePackWrite((id: string) => post<Pack>(`/api/packs/${id}/reopen`), "Reopened");
export type PackSectionItem = Omit<PackSection, "id" | "sortOrder"> & { id?: string };
export const useSavePackSections = (kind: "monthly" | "board" = "monthly") => useAdminWrite((items: PackSectionItem[]) => put(`/api/admin/pack-sections?kind=${kind}`, { items }), "Sections saved");
export const useReminders = () => useQuery({ queryKey: keys.reminders, queryFn: () => get<ReminderState>("/api/admin/reminders") });
export const useSaveReminders = () => useAdminWrite((body: Omit<ReminderSettings, "since">) => put("/api/admin/reminders", body), "Saved", [keys.reminders]);
export const useTestReminder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ to: string; subject: string }>("/api/admin/reminders/test"),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: keys.reminders });
      toast.success(`Test sent to ${r.to}`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
};
export const useSavePackSettings = () => useAdminWrite((body: PackSettings) => put("/api/admin/pack-settings", body), "Settings saved");

/* ------------------------------------------------------------------ */
/* Calendar and meetings                                               */
/* ------------------------------------------------------------------ */

export const useCalendar = (from: string, to: string) =>
  useQuery({ queryKey: keys.calendar(from, to), queryFn: () => get<{ items: CalItem[]; meetings: Meeting[]; upcoming: Meeting[] }>(`/api/calendar?from=${from}&to=${to}`), placeholderData: (prev) => prev });
export const useMeetingHistory = (id: string | null) => useQuery({ queryKey: keys.meetingHistory(id ?? ""), queryFn: () => get<AuditEntry[]>(`/api/meetings/${id}/history`), enabled: !!id });
function useMeetingWrite<V, R>(fn: (v: V) => Promise<R>, done?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of [keys.registers, keys.audit]) qc.invalidateQueries({ queryKey: k });
      for (const k of ["calendar", "meeting-history"]) qc.invalidateQueries({ queryKey: [k] });
      if (done) toast.success(done);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}
export type MeetingBody = Pick<Meeting, "title" | "date" | "start" | "end" | "guestRoles" | "guestIds" | "guestEmails" | "notes">;
export const useCreateMeeting = () => useMeetingWrite((b: MeetingBody & { kind: "board" | "other" }) => post<Meeting>("/api/meetings", b), "Meeting added");
export const useUpdateMeeting = () => useMeetingWrite(({ id, ...b }: MeetingBody & { id: string }) => put<Meeting>(`/api/meetings/${id}`, b), "Meeting saved");
export const useCancelMeeting = () => useMeetingWrite((id: string) => post<Meeting>(`/api/meetings/${id}/cancel`), "Meeting cancelled");
export const useSendInvite = () => useMeetingWrite((id: string) => post<{ result: string }>(`/api/meetings/${id}/invite`), "Invite sent");

/* Attachments on decisions and actions */

const plural = (e: AttachmentEntity) => (e === "decision" ? "decisions" : "actions");
export const useAttachments = (entity: AttachmentEntity, id: string | null) =>
  useQuery({ queryKey: keys.attachments(entity, id ?? ""), queryFn: () => get<AttachmentList>(`/api/${plural(entity)}/${id}/attachments`), enabled: !!id });
export const useAttachmentCounts = () => useQuery({ queryKey: keys.attachmentCounts, queryFn: () => get<Record<string, number>>("/api/attachments/counts") });

function useAttachmentWrite<V, R>(fn: (v: V) => Promise<R>, done?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const k of ["attachments", "attachment-counts", "decision-history", "action-history"]) qc.invalidateQueries({ queryKey: [k] });
      qc.invalidateQueries({ queryKey: keys.audit });
      if (done) toast.success(done);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work."),
  });
}

export const useAddAttachmentLink = () =>
  useAttachmentWrite(({ entity, id, url, name }: { entity: AttachmentEntity; id: string; url: string; name: string }) => post<Attachment>(`/api/${plural(entity)}/${id}/attachments/link`, { url, name }));
/** The file itself as the body; its name and type in headers, so neither is in the address. */
export const useUploadAttachment = () =>
  useAttachmentWrite(async ({ entity, id, file }: { entity: AttachmentEntity; id: string; file: File }) => {
    const res = await fetch(`/api/${plural(entity)}/${id}/attachments/upload`, {
      method: "POST",
      credentials: "same-origin",
      headers: { accept: "application/json", "content-type": "application/octet-stream", "x-otii-csrf": "1", "x-file-name": encodeURIComponent(file.name), "x-file-type": file.type || "application/octet-stream" },
      body: file,
    });
    const data = (await res.json().catch(() => null)) as (Attachment & { error?: string }) | null;
    if (!res.ok) throw new ApiError(res.status, res.status === 413 ? "Files can be up to 25 MB. Put bigger ones in Drive and add a link." : (data?.error ?? `Upload failed (${res.status}).`), null);
    return data as Attachment;
  }, "Uploaded");
export const useRemoveAttachment = () => useAttachmentWrite((id: string) => del(`/api/attachments/${id}`), "Removed");
